"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { ThemeProvider, useTheme } from "next-themes";
import { type CSSProperties, Suspense, useEffect, useState } from "react";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  initializeCloudWatchRum,
  setCloudWatchRumPage,
} from "@/lib/client/cloudwatch-rum";
import { getClientQueryClient } from "@/lib/client/query-client";

function CloudWatchRumProvider() {
  const pathname = usePathname();

  useEffect(() => {
    setCloudWatchRumPage(pathname);
  }, [pathname]);

  useEffect(() => {
    void initializeCloudWatchRum();
  }, []);

  return null;
}

function AppToaster() {
  const { resolvedTheme } = useTheme();

  return (
    <Toaster
      position="top-center"
      richColors
      closeButton
      theme={resolvedTheme === "dark" ? "dark" : "light"}
      style={
        {
          "--border-radius": "var(--radius-md)",
        } as CSSProperties
      }
      toastOptions={{
        closeButtonAriaLabel: "閉じる",
        style: {
          minWidth: "min(360px, calc(100vw - 32px))",
          maxWidth: "calc(100vw - 32px)",
        },
      }}
    />
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => getClientQueryClient());
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Suspense fallback={null}>
            <CloudWatchRumProvider />
          </Suspense>
          {children}
          <AppToaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
